import { toast } from "@/components/ui/use-toast";
import {
  FileSystemFileHandle,
  FileSystemDirectoryHandle
} from "file-system-access";
import { useCallback, useState } from "react";
import { stringify, parse } from "smol-toml";
import {
  Manifest,
  ManifestUnion,
  toManifest,
  BASE_MANIFEST,
  isFullManifest,
  isPythonManifest,
  MANIFEST_FILE_NAME,
  mergeManifests,
  MANIFEST_INCOMPLETE_ERROR,
  MANIFEST_CONVERSION_ERROR
} from "../types/manifest.type";
import { createTarBall } from "../utils/archive/tar";
import { bootstrapPythonProject } from "../utils/bootstrapPythonProject";
import { useDeployLocalProject } from "./deployLocalProject";

declare global {
  interface Window {
    showDirectoryPicker: () => Promise<FileSystemDirectoryHandle>;
  }
}

const TomlNotFoundError = new Error("numerous.toml not found");
TomlNotFoundError.name = "NotFoundError";

export const useLocalProjectFolder = () => {
  const [folderPath, setFolderPath] = useState<string>("");
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [manifestFileHandle, setManifestFileHandle] =
    useState<FileSystemFileHandle | null>(null);
  const [directoryHandle, setDirectoryHandle] =
    useState<FileSystemDirectoryHandle | null>(null);

  const deploy = useDeployLocalProject();

  const missingManifestFields = checkIfMissingManifestField(manifest);

  const setManifestFromToml = useCallback(
    async (toml: FileSystemFileHandle) => {
      const file = await toml.getFile();
      const content = await file.text();
      const parsedManifest = parse(content) as ManifestUnion;
      let [m, err] = toManifest(parsedManifest);

      if (err === MANIFEST_CONVERSION_ERROR) {
        toast({
          title: "Could not interpret numerous.toml",
          description:
            "Could not interpret the file from your folder. Using default numerous.toml"
        });
        m = BASE_MANIFEST;
      } else if (err === MANIFEST_INCOMPLETE_ERROR) {
        m = mergeManifests(m, BASE_MANIFEST);
      }

      setManifest(m);
      setManifestFileHandle(toml);
    },
    []
  );

  const selectFolder = useCallback(async () => {
    try {
      const dirHandle: FileSystemDirectoryHandle =
        await window.showDirectoryPicker();
      setFolderPath(dirHandle.name);
      setDirectoryHandle(dirHandle);

      const numerousToml = await dirHandle.getFileHandle(MANIFEST_FILE_NAME, {
        create: false
      });

      if (numerousToml) {
        setManifestFromToml(numerousToml);
      } else {
        throw TomlNotFoundError;
      }
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") return;
      if (
        (error instanceof Error && error.name === "NotFoundError") ||
        error === TomlNotFoundError
      ) {
        setManifest(BASE_MANIFEST);
      } else {
        setManifest(null);
        setManifestFileHandle(null);
        setFolderPath("");
        setDirectoryHandle(null);
        throw error;
      }
    }
  }, [setManifestFromToml]);

  const saveLocalFiles = useCallback(
    async (m: Partial<Manifest>) => {
      if (!manifest) throw new Error("no numerous manifest defined");
      if (!directoryHandle) throw new Error("No directory selected");

      const newManifest: Manifest = { ...manifest, ...m };
      if (!isFullManifest(newManifest)) throw MANIFEST_INCOMPLETE_ERROR;

      if (!missingManifestFields && manifestFileHandle) {
        const manifestToml = stringify(newManifest);
        const tomlFile = await manifestFileHandle.createWritable();
        await tomlFile.write(manifestToml);
        await tomlFile.close();
      } else if (isPythonManifest(newManifest)) {
        const fileHandlers = await bootstrapPythonProject(
          newManifest,
          directoryHandle
        );
        setManifestFileHandle(fileHandlers.manifestHandle);
      } else {
        throw new Error("Cannot bootstrap docker directories");
      }

      setManifest(newManifest);
    },
    [manifestFileHandle, manifest, directoryHandle, missingManifestFields]
  );

  const packageTar = useCallback(async () => {
    if (!directoryHandle || !manifest)
      throw new Error("no directory to create from");

    const blob = await createTarBall(directoryHandle, manifest.exclude);
    return blob;
  }, [directoryHandle, manifest]);

  const packageAndDeploy = useCallback(
    async (vars: {
      displayName: string;
      organizationSlug: string;
      appSlug: string;
      description: string;
    }) => {
      const tarBlob = await packageTar();
      const res = await deploy({ ...vars, tarBlob });

      return res;
    },
    [deploy, packageTar]
  );

  return {
    isSupported: Object.hasOwn(window, "showDirectoryPicker"),
    manifest,
    folderPath,
    selectFolder,
    deploy: packageAndDeploy,
    saveLocalFiles,
    missingManifestFields
  };
};

function checkIfMissingManifestField(m: Manifest | null): boolean {
  if (m === null) return false;
  if (m.port === 0 || m.port === undefined || !m.cover_image || !m.name)
    return true;
  if ("python" in m) {
    const p = m.python;
    return !p.app_file || !p.library || !p.requirements_file || !p.app_file;
  }

  return false;
}
